import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Star, 
  MapPin, 
  Building, 
  User, 
  HomeIcon,
  BadgeCheck,
  ChevronRight,
  Sparkles,
  Award,
  Shield,
  Verified
} from 'lucide-react';

const PartnerCard = ({ partner }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    let baseRoute;
    switch(partner.tipo_usuario_id) {
      case 2:
        baseRoute = '/vendedor/';
        break;
      case 3:
        baseRoute = '/constructora/';
        break;
      case 4:
        baseRoute = '/inmobiliaria/';
        break;
      default:
        baseRoute = '/constructora/';
    }
    
    navigate(`${baseRoute}${partner.nombre_usuario}`);
  };

  // Configuración premium de colores y tipos de usuarios
  const typeConfig = {
    2: { // Vendedor
      name: 'Vendedor',
      icon: User,
      colors: {
        gradient: 'from-blue-500 via-blue-600 to-indigo-600',
        gradientHover: 'from-blue-600 via-blue-700 to-indigo-700',
        glow: 'shadow-blue-500/30',
        glowHover: 'shadow-blue-500/50',
        badge: 'from-blue-500/20 to-indigo-500/20',
        badgeText: 'text-blue-600 dark:text-blue-400',
        accent: 'text-blue-600 dark:text-blue-400',
        ring: 'ring-blue-500/50',
        shimmer: 'from-blue-400/0 via-blue-400/20 to-blue-400/0'
      }
    },
    3: { // Constructora
      name: 'Constructora',
      icon: Building,
      colors: {
        gradient: 'from-amber-500 via-orange-500 to-red-500',
        gradientHover: 'from-amber-600 via-orange-600 to-red-600',
        glow: 'shadow-amber-500/30',
        glowHover: 'shadow-amber-500/50',
        badge: 'from-amber-500/20 to-orange-500/20',
        badgeText: 'text-amber-600 dark:text-amber-400',
        accent: 'text-amber-600 dark:text-amber-400',
        ring: 'ring-amber-500/50',
        shimmer: 'from-amber-400/0 via-amber-400/20 to-amber-400/0'
      }
    },
    4: { // Inmobiliaria
      name: 'Inmobiliaria',
      icon: HomeIcon,
      colors: {
        gradient: 'from-purple-500 via-violet-500 to-pink-500',
        gradientHover: 'from-purple-600 via-violet-600 to-pink-600',
        glow: 'shadow-purple-500/30',
        glowHover: 'shadow-purple-500/50',
        badge: 'from-purple-500/20 to-pink-500/20',
        badgeText: 'text-purple-600 dark:text-purple-400',
        accent: 'text-purple-600 dark:text-purple-400',
        ring: 'ring-purple-500/50',
        shimmer: 'from-purple-400/0 via-purple-400/20 to-purple-400/0'
      }
    }
  };

  // Obtener configuración para este partner
  const config = typeConfig[partner.tipo_usuario_id] || typeConfig[3];
  const PartnerTypeIcon = config.icon;
  
  // Formato para calificación
  const formattedRating = parseFloat(partner.calificacion_promedio || 4.5).toFixed(1);
  const ratingStars = Math.floor(formattedRating);
  
  // Verificaciones
  const isVerified = partner.email_verificado || partner.telefono_verificado;
  const verificationCount = (partner.email_verificado ? 1 : 0) + (partner.telefono_verificado ? 1 : 0);

  return (
    <motion.div
      onClick={handleClick}
      className="group cursor-pointer h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      transition={{ 
        duration: 0.3, 
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      <div className="relative h-full overflow-hidden">
        {/* Main Card with Clean Button Shadow */}
        <motion.div 
          className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-black/30
                    border border-gray-200 dark:border-gray-700 h-full flex flex-col overflow-hidden
                    transition-all duration-300 hover:shadow-xl dark:hover:shadow-black/50"
          whileHover={{ 
            y: -1,
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
          }}
        >
          {/* Subtle Gradient Background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${config.colors.gradient} opacity-[0.02] dark:opacity-[0.03]`} />

          {/* Header Section */}
          <div className="relative p-6 pb-4">
            {/* Type Badge */}
            <motion.div 
              className={`absolute top-4 right-4 bg-gradient-to-r ${config.colors.gradient} 
                        rounded-xl px-3 py-1.5 flex items-center gap-2 shadow-md dark:shadow-black/30`}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <PartnerTypeIcon className="w-4 h-4 text-white" />
              <span className="text-sm font-semibold text-white">
                {config.name}
              </span>
            </motion.div>
            
            {/* Profile Section */}
            <div className="flex items-center gap-4 mt-2">
              {/* Avatar with Premium Ring */}
              <div className="relative">
                {/* Animated Glow Ring */}
                <motion.div 
                  className={`absolute inset-0 rounded-full bg-gradient-to-r ${config.colors.gradient} blur-md opacity-60`}
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.6, 0.8, 0.6],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                
                {/* Ring Border */}
                <div className={`relative p-1 rounded-full bg-gradient-to-r ${config.colors.gradient} shadow-lg dark:shadow-black/50`}>
                  <div className="bg-white dark:bg-gray-800 rounded-full p-1">
                    <div className="relative h-16 w-16 rounded-full overflow-hidden">
                      <motion.img
                        src={imageError ? '/default-avatar.png' : (partner.foto_perfil || '/default-avatar.png')}
                        alt={partner.nombre}
                        className="w-full h-full object-cover"
                        onError={() => setImageError(true)}
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Verification Badge */}
                {isVerified && (
                  <motion.div 
                    className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-800 rounded-full p-1 shadow-lg dark:shadow-black/50"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring" }}
                  >
                    <div className={`p-1 rounded-full bg-gradient-to-r ${config.colors.gradient}`}>
                      <Verified className="w-3 h-3 text-white" />
                    </div>
                  </motion.div>
                )}
              </div>
              
              {/* Name and Rating */}
              <div className="flex-1 min-w-0">
                <motion.h3 
                  className="text-lg font-bold text-gray-800 dark:text-gray-200 truncate mb-1"
                  whileHover={{ scale: 1.02 }}
                >
                  {partner.nombre}
                </motion.h3>
                
                {/* Premium Rating Display */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Star
                          className={`w-4 h-4 transition-colors duration-200 ${
                            index < ratingStars 
                              ? `${config.colors.accent} fill-current drop-shadow-sm` 
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        />
                      </motion.div>
                    ))}
                  </div>
                  <span className={`text-sm font-semibold ${config.colors.accent}`}>
                    {formattedRating}
                  </span>
                </div>
                
                {/* Verification Status */}
                {isVerified && (
                  <motion.div 
                    className="flex items-center gap-1 mt-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Shield className={`w-3 h-3 ${config.colors.accent}`} />
                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                      {verificationCount > 1 ? 'Completamente verificado' : 'Verificado'}
                    </span>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
          
          {/* Content Section */}
          <div className="relative flex-1 px-6 pb-6 flex flex-col">
            {/* Location */}
            {partner.ubicacion && (
              <motion.div 
                className="flex items-start gap-2 mb-4"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                  {partner.ubicacion}
                </p>
              </motion.div>
            )}
            
            {/* Slogan */}
            <motion.p 
              className="text-gray-700 dark:text-gray-300 text-sm mb-6 flex-1 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {partner.slogan || "Construyendo el futuro con excelencia y dedicación"}
            </motion.p>
            
            {/* Footer */}
            <motion.div 
              className="mt-auto pt-4 border-t border-gray-200/50 dark:border-gray-600/50 flex justify-between items-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {/* Properties Badge */}
              <div className={`bg-gradient-to-r ${config.colors.gradient} rounded-xl px-3 py-2 
                            flex items-center gap-2 shadow-sm dark:shadow-black/20`}>
                <Award className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-white">
                  {partner.total_propiedades || 0} Propiedades
                </span>
              </div>
              
              {/* Action Button */}
              <motion.div 
                className={`flex items-center gap-1 ${config.colors.accent} font-semibold text-sm 
                          transition-all duration-300 group-hover:gap-2`}
                whileHover={{ x: 5 }}
              >
                <span>Ver Perfil</span>
                <motion.div
                  animate={{ x: [0, 3, 0] }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                >
                  <ChevronRight className="w-4 h-4" />
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Subtle Floating Sparkles Effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-50">
          {[...Array(2)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                top: `${30 + i * 40}%`,
                right: `${15 + i * 20}%`,
              }}
              animate={{
                y: [0, -8, 0],
                opacity: [0, 0.4, 0],
                scale: [0.8, 1, 0.8],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 1.5,
                ease: "easeInOut"
              }}
            >
              <Sparkles className={`w-3 h-3 ${config.colors.accent} opacity-60`} />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default PartnerCard;